import React, { useEffect } from "react"

import { Avatar, Flex, Modal, Spin, theme, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import { logoutThunk } from "../../Containers/LoginPage/redux/auth.thunks"
import { settingActions } from "../../Containers/Setting/redux/setting.slice"
import {
  acceptInviteUser,
  getInviteUserInfo,
  logoutInviteUser,
  rejectInviteUser
} from "../../Containers/Setting/redux/setting.thunks"
import { getUserInfo } from "../../Containers/User/redux/user.thunks"
import Icon from "../../Icon"
import { useQuery } from "../../Utils/utils"
import ButtonComponent from "../Button"

const { Title, Text } = Typography

const AcceptInviteModal = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { token } = theme?.useToken()
  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const query = useQuery(search)
  const invite = query.get("invite")

  const isMobile = useSelector((state) => state.nav.isMobile)
  const userInfo = useSelector((store) => store?.auth?.userInfo)
  const { inviteInfoFetching, inviteInfo, saveFetching, saveResult } = useSelector(
    (state) => state.setting
  )
  const anotherAccount =
    !!userInfo && !!inviteInfo && userInfo?.email !== inviteInfo?.["invited_email"]
  const openModal = !!inviteInfo

  useEffect(() => {
    if (userInfo && invite) {
      dispatch(getInviteUserInfo(invite))
    }
  }, [userInfo?.email])

  useEffect(() => {
    if (saveResult && ["invite_accept", "invite_reject"]?.includes(saveResult)) {
      navigate(pathname, { replace: true })
      dispatch(settingActions.settingsSaveResultReset())
      dispatch(getUserInfo())
    }
  }, [saveResult])

  const handleCloseModal = () => {
    dispatch(settingActions.clearInviteUserInfo())
    if (anotherAccount) {
      dispatch(logoutInviteUser(inviteInfo?.id))
      navigate(pathname, { replace: true })
    } else {
      dispatch(rejectInviteUser(inviteInfo?.id))
    }
  }

  const handleAcceptInvite = () => {
    if (anotherAccount) {
      dispatch(logoutInviteUser(inviteInfo?.id))
      dispatch(logoutThunk())
    } else {
      dispatch(acceptInviteUser(inviteInfo?.id))
    }
  }

  return (
    <Modal
      {...(isMobile ? { transitionName: "" } : null)}
      open={openModal}
      centered={!isMobile}
      width={442}
      title={null}
      footer={null}
      closeIcon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
      closable="true"
      onCancel={handleCloseModal}
      destroyOnHidden
      classNames={{
        content: "clear-bg full-width-header bg-center"
      }}
    >
      <Spin size="large" spinning={inviteInfoFetching || saveFetching} wrapperClassName="visible">
        <Flex vertical={true} justify={"center"} align={"center"} style={{ paddingTop: 52 }}>
          {anotherAccount ? (
            <Flex vertical={true} gap={12}>
              <Text>
                {t("You have been invited to a business account using a different email address.")}
              </Text>
              <Text>
                {t(
                  "Please log out of your current Chargeback account and click the “Accept Invite” button in the invitation email again to proceed."
                )}
              </Text>
            </Flex>
          ) : (
            <>
              <Avatar
                className="user_avatar"
                size={56}
                style={{
                  backgroundColor: inviteInfo?.business_account?.photo ? "transparent" : "#225AEA",
                  borderRadius: 12,
                  border: "none",
                  fontSize: 14,
                  minWidth: 56,
                  marginBottom: 24
                }}
                {...(inviteInfo?.business_account?.photo && {
                  src: inviteInfo?.business_account?.photo
                })}
              >
                {!inviteInfo?.business_account?.photo
                  ? inviteInfo?.business_account?.name?.[0]
                  : null}
              </Avatar>

              <Title level={4} style={{ marginBottom: 8 }}>
                {t("Join your team on Chargeback")}
              </Title>

              <Text type="secondary" style={{ textAlign: "center" }}>
                {inviteInfo?.["inviter_full_name"]}
                <Text strong style={{ padding: "0 3px" }}>
                  ({inviteInfo?.["inviter_email"]})
                </Text>
                {`has invited you to use Chargeback with them, in a workspace called ${inviteInfo?.business_account?.name}.`}
              </Text>

              <Flex
                align="center"
                justify="space-between"
                style={{
                  width: "100%",
                  padding: "10px 16px 10px 10px",
                  marginTop: 24,
                  border: `1px solid`,
                  borderColor: token?.colorBorder,
                  borderRadius: token?.radiusBase
                }}
              >
                <Flex gap={12} align="center">
                  <Avatar
                    className="user_avatar"
                    size={40}
                    style={{
                      backgroundColor: inviteInfo?.business_account?.photo
                        ? "transparent"
                        : "#225AEA",
                      borderRadius: 12,
                      border: "none",
                      fontSize: 14,
                      minWidth: 40
                    }}
                    {...(inviteInfo?.business_account?.photo && {
                      src: inviteInfo?.business_account?.photo
                    })}
                  >
                    {!inviteInfo?.business_account?.photo
                      ? inviteInfo?.business_account?.name?.[0]
                      : null}
                  </Avatar>
                  <Text strong style={{ fontSize: 16 }}>
                    {inviteInfo?.business_account?.name}
                  </Text>
                </Flex>

                <Text type="secondary">{`${inviteInfo?.business_account?.members} ${t("members")}`}</Text>
              </Flex>
            </>
          )}

          <Flex gap={12} style={{ width: "100%", margin: "40px 0 12px", padding: "0 2px" }}>
            <ButtonComponent size="large" className="flex center" onClick={handleCloseModal} block>
              {t("Cancel")}
            </ButtonComponent>

            <ButtonComponent
              size="large"
              type="primary"
              className="flex center"
              onClick={handleAcceptInvite}
              block
            >
              {t(anotherAccount ? "Logout" : "Accept invite")}
            </ButtonComponent>
          </Flex>
        </Flex>
      </Spin>
    </Modal>
  )
}

export default React.memo(AcceptInviteModal)
